import { DEFAULT_RUN_SETTINGS, type RunSettings } from "@/lib/run-settings";
import { atom } from "jotai";
import { atomFamily, atomWithStorage, selectAtom } from "jotai/utils";
import type { Edge } from "@xyflow/react";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import type { SaveState } from "@/types/spotflow";
import type {
  NodeProgress,
  IsValidResponse,
  NameConflict,
  NodeDefinition,
} from "@/generated";
import type { PickerHighlight, DynamicNodeConfig } from "./types";

export type { DynamicNodeConfig };

// ── Core editor state ──────────────────────────────────────────────────────

export const initialNodes: NoxusReactflowNodeV2[] = [];
export const initialEdges: Edge[] = [];

export const nodesAtom = atom<NoxusReactflowNodeV2[]>(initialNodes);
export const edgesAtom = atom<Edge[]>(initialEdges);
export const activeFlowIdAtom = atom<string | undefined>(undefined);
// Flow type of the workflow being edited — gates which nodes can be added
// (NodeDefinition.allowed_in_flow_types).
export const workflowTypeAtom = atom<string | undefined>(undefined);

export const pendingLayoutFlowIdAtom = atom<string | null>(null);

export const canvasMeasuredAtom = atom((get) => {
  const visible = get(nodesAtom).filter((n) => !n.hidden);
  return (
    visible.length > 0 &&
    visible.every((n) => n.measured?.width && n.measured?.height)
  );
});

const nodeTypeSetsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((t) => b.has(t));

// Distinct node types on the canvas, with set-equality so consumers (node
// pickers filtering `unique` nodes) don't re-render on drags/config edits.
export const existingNodeTypesAtom = selectAtom(
  nodesAtom,
  (nodes) => new Set(nodes.map((n) => n.data.nodeType)),
  nodeTypeSetsEqual
);
export const saveStateAtom = atom<SaveState>({
  timestamp: undefined,
  status: "not_saved",
});
export const hasQueueToSaveAtom = atom<boolean>(false);
// Whether the unsaved changes include one the user made, rather than only ones
// derived from the flow (an auto-layout, iterate flags). Only then can losing a
// save race to a newer version cost the user work.
export const hasUnsavedUserEditAtom = atom<boolean>(false);
// Bumped by every edit (see `markDirty`) and by leaving a flow. A save
// compares it across its own await to tell "nothing moved" from "the user
// kept typing while the PATCH was in flight".
export const editorRevisionAtom = atom<number>(0);
export const hoveredNodeAtom = atom<string | undefined>(undefined);
export const edgeHighlightNodeAtom = atom<string | undefined>(undefined);

/** Source of an in-flight edge drag, set on `onConnectStart` and cleared on `onConnectEnd`. */
export const edgeDragSourceAtom = atom<{
  nodeId: string;
  handleId: string | null;
} | null>(null);

/** Node whose body is currently a valid drop target for the in-flight edge drag. */
export const edgeDropTargetNodeIdAtom = atom<string | null>(null);

/** Group frame under the centre of the node being dragged. */
export const dragOverContainerIdAtom = atom<string | null>(null);

export const selectedLoopIterationsAtom = atom<Record<string, number>>({});

export const openPickerAtPositionAtom = atom<
  | ((
      screenPos: { x: number; y: number },
      flowPos: { x: number; y: number },
      // Group the new node should join (picker opened from inside a frame).
      parentGroupId?: string
    ) => void | Promise<void>)
  | null
>(null);

export const pickerHighlightAtom = atom<PickerHighlight>({
  sourceNodeId: undefined,
  sourceOutputName: undefined,
  targetNodeId: undefined,
  targetInputName: undefined,
});

/** Set when hovering "Run until here" — tracks which node is the run target */
export const hoveredPartialRunNodeAtom = atom<string | undefined>(undefined);
export const renamingNodeIdAtom = atom<string | null>(null);
export const runSettingsAtom = atom<RunSettings>(DEFAULT_RUN_SETTINGS);

export const saveWorkflowHashAtom = atom<string | undefined>(undefined);

/** The server `revision` this canvas was loaded from (refreshed on every save
 * response). Sent as `base_revision` on saves — the server 409s when the row
 * has moved on. Identity-exact, unlike the content hash. */
export const saveBaseRevisionAtom = atom<number | undefined>(undefined);
/** Set when a save is rejected with 409 (someone else saved a newer version);
 *  drives the conflict-resolution dialog. */
export const saveConflictAtom = atom<boolean>(false);
export const selectedNodeIdAtom = atom<string | null>(null);
export const runNodeStatusesAtom = atom<Record<string, NodeProgress>>({});

/**
 * In preview mode, clicking a canvas node focuses its row in the run-drawer
 * Progress tab (auto-scroll + expand + brief pulse). The `nonce` lets the same
 * node be re-focused on a second click.
 */
export const progressFocusedNodeAtom = atom<{
  nodeId: string;
  nonce: number;
} | null>(null);

export const seeNodeInFlowAtom = atom<{
  nodeId: string;
  nonce: number;
} | null>(null);

/** Per-node accumulated streaming content for the active run.
 *
 * Populated by the SSE consumer in `useRunV2` from `type=content` events on
 * the run's Redis content stream. Iteration is keyed alongside the node ID so
 * fan-out iterations don't bleed into each other (currently only iteration 0
 * streams — see flow_v2/executor.py).
 */
export type NodeStreamKey = { nodeId: string; iteration: number };

const nodeStreamKey = (k: NodeStreamKey) => `${k.nodeId}::${k.iteration}`;

/**
 * Per-(node, iteration) atom family of accumulated chunk text.
 * Keyed by `nodeId::iteration` so equality is structural.
 */
export const nodeContentStreamFamilyAtom = atomFamily(
  (_key: NodeStreamKey) => atom<string>(""),
  (a, b) => nodeStreamKey(a) === nodeStreamKey(b)
);

/**
 * All node IDs currently producing chunks for the active run. Drives the
 * "is this node streaming?" query without forcing every consumer to subscribe
 * to the full nodeContentStreamFamilyAtom space.
 */
export const streamingNodeIdsAtom = atom<Set<string>>(new Set<string>());

export const appendNodeContentStreamAtom = atom(
  null,
  (
    _get,
    set,
    { nodeId, iteration, content }: NodeStreamKey & { content: string }
  ) => {
    set(
      nodeContentStreamFamilyAtom({ nodeId, iteration }),
      (previous) => previous + content
    );
    set(streamingNodeIdsAtom, (previous) => {
      if (previous.has(nodeId)) return previous;
      return new Set(previous).add(nodeId);
    });
  }
);

export const resetNodeContentStreamsAtom = atom(null, (get, set) => {
  for (const nodeId of get(streamingNodeIdsAtom)) {
    nodeContentStreamFamilyAtom.remove({ nodeId, iteration: 0 });
  }
  set(streamingNodeIdsAtom, new Set<string>());
});

export const isValidDataAtom = atom<IsValidResponse | undefined>(undefined);

/** Backend-authoritative V2 flow validation (the four rules), keyed by node. */
export interface FlowValidationNode {
  node_id: string;
  field_errors: Record<string, string>;
  node_errors: string[];
  // Non-blocking (Rules 10–11): shown to the user, never gates Run.
  field_warnings?: Record<string, string>;
  node_warnings?: string[];
}
export interface FlowValidationResult {
  valid: boolean;
  nodes: Record<string, FlowValidationNode>;
  // Rule 14: same-type flow inputs / outputs that share a name.
  name_conflicts?: NameConflict[];
}
/** Latest result from the V2 validate endpoint; null until first validated. */
export const flowValidationV2Atom = atom<FlowValidationResult | null>(null);
/** Set when user clicks "Preview in editor" on a past run — drives the preview banner and node I/O display */
export const previewRunIdAtom = atom<string | null>(null);

/** Snapshot of nodes/edges before entering preview mode — restored on exit.
 * Stamped with the flow it was captured from: these atoms are global and
 * survive navigation, so restoring a snapshot that belongs to another flow
 * would hand a foreign definition to the autosave. */
export const previewSnapshotAtom = atom<{
  flowId: string;
  nodes: NoxusReactflowNodeV2[];
  edges: Edge[];
} | null>(null);

/** Set when the editor is viewing a saved version (via `?version=` query param).
 * Loaded into the canvas as the workflow definition, but the canvas must be
 * read-only — saves to the live workflow are forbidden until the user restores
 * or returns to the current version. */
export const viewingVersionIdAtom = atom<string | null>(null);

export const loadedVersionIdAtom = atom<string | null>(null);

/** Inspecting a past run on the canvas (I/O panels, progress, etc.). */
export const isPreviewRunModeAtom = atom((get) => !!get(previewRunIdAtom));

/** Viewing a saved version snapshot (`?version=`). Fully frozen. */
export const isViewingVersionAtom = atom((get) => !!get(viewingVersionIdAtom));

/** Structural edits forbidden: add/delete/connect/config/paste/shortcuts/save.
 * True in run-preview and version-view. Use for every "can the user change
 * the workflow?" gate except canvas drag (see `isCanvasNodesDraggableAtom`).
 * Preview-only visuals should keep reading `previewRunIdAtom` directly. */
export const isEditorReadOnlyAtom = atom(
  (get) => get(isPreviewRunModeAtom) || get(isViewingVersionAtom)
);

/** Nodes may be dragged on the canvas. Allowed in normal edit and run-preview
 * (reposition to uncover I/O panels); blocked when viewing a saved version. */
export const isCanvasNodesDraggableAtom = atom(
  (get) => !get(isViewingVersionAtom)
);

/** Node registry — set by EditorV2Inner, read by BaseNodeV2 for validation */
export const nodeRegistryAtom = atom<NodeDefinition[]>([]);

/** Exposed run callbacks — set by EditorV2Inner, read by BaseNodeV2 hover toolbar */
export const runTriggersAtom = atom<{
  requestPartialRun: (nodeId: string) => void;
  requestSingleNodeRun: (nodeId: string) => void;
  isRunning: boolean;
} | null>(null);

/** Exposed picker callback — set by EditorV2Inner, read by BaseNodeV2 to open picker from "+" button */
export const openPickerNearNodeAtom = atom<
  | ((
      screenPos: { x: number; y: number },
      flowPos: { x: number; y: number },
      sourceNodeId: string,
      sourceHandleId?: string | null,
      // When the drag started from a target handle, the new node is the source
      // of the pre-wired edge instead of the target.
      sourceHandleType?: "source" | "target",
      // "adjacent" snaps beside the source node (spacebar, + handle).
      // "atCursor" keeps the passed screen position (edge drag onto empty canvas).
      placement?: "adjacent" | "atCursor",
      // Group the new node should join (edge dropped inside a group frame).
      parentGroupId?: string
    ) => void | Promise<void>)
  | null
>(null);

/** Node ID whose context menu is currently open — keeps the node in hovered state */
export const contextMenuNodeIdAtom = atom<string | null>(null);

/** Node ID highlighted by hovering over a variable chip — only adds a visual ring, no layout changes */
export const chipHighlightNodeIdAtom = atom<string | null>(null);

/** Requested config panel section — set by node zone clicks, consumed by NodeConfigPanelV2 */
export const requestedSectionAtom = atom<string | null>(null);

export const secretsPanelOpenAtom = atom<boolean>(false);

/** Simple mode — compact nodes showing only name + icon; full card shown on hover */
export const simpleModeAtom = atom<boolean>(false);

/** Canvas edge style — bezier when true, step when false */
export const canvasUseSmoothEdgesAtom = atom<boolean>(true);

/** Canvas scroll gesture — scrolling zooms when true, pans when false */
export const canvasZoomModeAtom = atomWithStorage<boolean>(
  "editor-v2-zoom-mode",
  false
);

// ── Dynamic node configs ──────────────────────────────────────────────────

/** Per-node overrides returned by a node's config_endpoint — merged on top of the registry definition */
export const dynamicNodeConfigsAtom = atom<Map<string, DynamicNodeConfig>>(
  new Map()
);

/**
 * Per-node config-driven card identity (title + icon) from the config_endpoint
 * — used by the canvas card so a generic node (e.g. the MCP tool) reflects the
 * picked integration/tool. Memoized per node so only the affected card
 * re-renders when one node's config changes, not every card on the canvas.
 */
export const nodeDynamicDisplayAtom = atomFamily((nodeId: string) =>
  selectAtom(
    dynamicNodeConfigsAtom,
    (m): { title: string | null; image: string | null } => {
      const o = m.get(nodeId);
      return { title: o?.title ?? null, image: o?.image ?? null };
    },
    (a, b) => a.title === b.title && a.image === b.image
  )
);

/** Node IDs whose config_endpoint is currently being fetched (refresh button + drawer spinners) */
export const nodesFetchingConfigAtom = atom<string[]>([]);

/** True once the batch config-endpoint load completed for the current canvas */
export const areConfigEndpointsLoadedAtom = atom<boolean>(false);

/** Per-node loading selector for card identity/config chrome. */
export const nodeConfigLoadingAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const initialBatchPending = !get(areConfigEndpointsLoadedAtom);
    return initialBatchPending || get(nodesFetchingConfigAtom).includes(nodeId);
  })
);

/** Bumped on every real canvas load (not on self-saves); triggers the batch
 *  config-endpoint load */
export const flowLoadGenerationAtom = atom<number>(0);

/**
 * Per-node config-endpoint error message — populated when a node's
 * config_endpoint call rejects (e.g. backend 500 trying to load a V2 subflow
 * definition). Cleared on the next successful fetch for that node so the
 * banner disappears once the underlying issue is resolved.
 */
export const nodeConfigErrorsAtom = atom<Map<string, string>>(new Map());

/** Node IDs queued for an immediate config_endpoint refresh (e.g. new InvokeSubflowNode). */
export const pendingNodeConfigRefreshAtom = atom<string[]>([]);

export const requestNodeConfigRefreshAtom = atom(
  null,
  (get, set, nodeId: string) => {
    const pending = get(pendingNodeConfigRefreshAtom);
    if (pending.includes(nodeId)) return;
    set(pendingNodeConfigRefreshAtom, [...pending, nodeId]);
  }
);
